import { css, type SerializedStyles } from '@emotion/react'
import { cardActionAreaClasses, linkClasses } from '@mui/material'
import { type Theme } from '@mui/material/styles'
import { rem, size } from 'polished'

import { RADIUS_0, RADIUS_1 } from '@Styles/constants/radius'
import { getFocusVisibleStyle } from '@Styles/focus'
import { type Surface } from '@Types/surface'

import { type Align } from './LinkBase.types'

const ICON_SIZE = rem(16)
const ICON_SPACING = rem(6)

export const link = (
  theme: Theme,
  surface: Surface,
  isInline: boolean,
  hasDefaultFocusVisible: boolean,
  align: Align,
): SerializedStyles =>
  css(
    {
      '&:hover': {
        color: surface === 'onPrimary' ? theme.customPalette.onElements.selected : theme.palette.primary.dark,
      },
      '&:hover, &:focus, &:active': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
      cursor: 'pointer',
      justifyContent: align,
      padding: `calc(${theme.spacing(2)} - ${rem(1)})`,
      position: 'relative',
    },
    isInline
      ? { display: 'inline' }
      : {
          alignItems: 'center',
          display: 'inline-flex',
        },
    hasDefaultFocusVisible
      ? {
          '&:focus-visible': {
            borderRadius: rem(2),
            outline: `2px solid ${
              surface === 'onPrimary' ? theme.palette.primary.contrastText : theme.palette.primary.main
            }`, // Same as focusVisible outline's style
          },
        }
      : {
          [`&.${linkClasses.focusVisible} .${cardActionAreaClasses.focusHighlight}`]: {
            opacity: 0,
          },
          '&:focus-visible': {
            outline: 'none',
          },
          '&:focus-visible::before': getFocusVisibleStyle(
            surface === 'onPrimary' ? theme.palette.primary.contrastText : theme.palette.primary.main,
            RADIUS_1,
            '0',
          ),
        },
  )

export const fullWidth = css({
  width: '100%',
})

export const noSpacing = (theme: Theme, hasDefaultFocusVisible: boolean): SerializedStyles =>
  css(
    {
      padding: 0,
    },
    !hasDefaultFocusVisible && {
      '&:focus-visible::before': getFocusVisibleStyle(theme.palette.primary.main, RADIUS_0, `-${theme.spacing(0)}`),
    },
  )

export const icon = (isInline: boolean): SerializedStyles =>
  css(
    {
      ...size(ICON_SIZE),
      alignSelf: 'normal',
      fill: 'currentcolor',
      flexShrink: 0,
      marginLeft: ICON_SPACING,
    },
    isInline && { position: 'relative', top: '3px' },
  )
